import { toaster } from "@/_components/lib/ui/toaster";
import type { EmojiKey } from "@/_components/ui/Emoji/emojiList";

/**
 * Rank tiers are based on Top % (lower = better).
 * Elite ≤10 | Outstanding ≤25 | Strong ≤50 | Developing ≤75 | Emerging >75
 *
 * API `percentile` = Top % (e.g. 6.6 means Top 6.6%).
 * passedPercent = 100 - topPercent (e.g. 93.4% of candidates outperformed).
 */
export type IndustryRank =
  | "elite"
  | "outstanding"
  | "strong"
  | "developing"
  | "emerging";

export type RankingDisplayMode = "outstanding" | "completed";

export interface IndustryRankSegment {
  rank: IndustryRank;
  /** Width of this segment as % of the bar (left = worst → right = best). */
  widthPercent: number;
  /** Accent color sampled from the ranking bar gradient for badges/labels. */
  barColor: string;
  badgeBg: string;
  badgeBorder: string;
  badgeFg: string;
  emoji: EmojiKey;
  labelRange: string;
}

export interface RankingDisplayModel {
  /** Top rank share, e.g. 6.6 (best performers). From API `percentile`. */
  topPercent: number;
  /** % of candidates outperformed, e.g. 93.4. */
  passedPercent: number;
  topPercentLabel: string;
  passedPercentLabel: string;
  /** True when Top % ≤ 25 — safe to celebrate with "TOP X%". */
  isOutstanding: boolean;
  showTopPercent: boolean;
  mode: RankingDisplayMode;
}

/** Product ranking palette for the share card scale. */
export const RANKING_PALETTE = {
  obsidian: "#0B0B12",
  cloud: "#FFFFFF",
  cloudMuted: "#D6D8E2",
  fog: "#787C92",
  fogLight: "#8B8F9F",
  /** Accent — CTA, marker, highlight. */
  signalViolet: "#5B21F4",
  /** End of the scale (highest tier). */
  auroraTeal: "#4ADEC2",
} as const;

/**
 * Continuous bar: Fog (low) → Signal Violet → Aurora Teal (elite).
 * Applied as a single background on the track (not per-segment fills).
 */
export const RANK_BAR_GRADIENT = `linear-gradient(90deg, ${RANKING_PALETTE.fog} 0%, #6356C8 38%, ${RANKING_PALETTE.signalViolet} 62%, #3CC9B8 88%, ${RANKING_PALETTE.auroraTeal} 100%)`;

/** Celebrate "TOP X%" only in the top quartile. */
export const OUTSTANDING_TOP_PERCENT_MAX = 25;

/**
 * Bar segments left → right: Emerging (worst) → Elite (best).
 * Widths match tier ranges so the marker maps linearly to Top %.
 * `barColor` is a gradient-aligned accent for badges (the track itself uses RANK_BAR_GRADIENT).
 */
export const INDUSTRY_RANK_SEGMENTS: IndustryRankSegment[] = [
  {
    rank: "emerging",
    widthPercent: 25,
    barColor: RANKING_PALETTE.fog,
    badgeBg: "rgba(120,124,146,0.18)",
    badgeBorder: RANKING_PALETTE.fog,
    badgeFg: RANKING_PALETTE.cloudMuted,
    emoji: "seedling",
    labelRange: "Top 75%+",
  },
  {
    rank: "developing",
    widthPercent: 25,
    barColor: "#6356C8",
    badgeBg: "rgba(99,86,200,0.2)",
    badgeBorder: "#6356C8",
    badgeFg: RANKING_PALETTE.cloudMuted,
    emoji: "chart-increasing",
    labelRange: "50–75%",
  },
  {
    rank: "strong",
    widthPercent: 25,
    barColor: RANKING_PALETTE.signalViolet,
    badgeBg: "rgba(91,33,244,0.2)",
    badgeBorder: RANKING_PALETTE.signalViolet,
    badgeFg: RANKING_PALETTE.cloud,
    emoji: "flexed-biceps-default",
    labelRange: "25–50%",
  },
  {
    rank: "outstanding",
    widthPercent: 15,
    barColor: "#4F3AE8",
    badgeBg: "rgba(91,33,244,0.28)",
    badgeBorder: RANKING_PALETTE.signalViolet,
    badgeFg: RANKING_PALETTE.cloud,
    emoji: "star",
    labelRange: "10–25%",
  },
  {
    rank: "elite",
    widthPercent: 10,
    barColor: RANKING_PALETTE.auroraTeal,
    badgeBg: "rgba(74,222,194,0.18)",
    badgeBorder: RANKING_PALETTE.auroraTeal,
    badgeFg: RANKING_PALETTE.auroraTeal,
    emoji: "gem-stone",
    labelRange: "0–10%",
  },
];

export function clampPercent(value: number): number {
  if (Number.isNaN(value) || !Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, value));
}

/** @deprecated Use clampPercent */
export const clampTopPercent = clampPercent;

/** Always 1 decimal place per product requirement. */
export function formatPercentOneDecimal(value: number): string {
  return clampPercent(value).toFixed(1);
}

/**
 * Build UI-ready ranking from API Top % (lower = better).
 * Example: top 6.6% → passed 93.4%.
 */
export function buildRankingDisplayFromTopPercent(
  topPercentInput: number,
): RankingDisplayModel {
  const topPercent = clampPercent(topPercentInput);
  const passedPercent = clampPercent(100 - topPercent);
  const isOutstanding = topPercent <= OUTSTANDING_TOP_PERCENT_MAX;

  return {
    topPercent,
    passedPercent,
    topPercentLabel: formatPercentOneDecimal(topPercent),
    passedPercentLabel: formatPercentOneDecimal(passedPercent),
    isOutstanding,
    showTopPercent: isOutstanding,
    mode: isOutstanding ? "outstanding" : "completed",
  };
}

/**
 * Build from "passed %" (candidates outperformed).
 * Example: 93.4 → Top 6.6%.
 */
export function buildRankingDisplayFromPassedPercent(
  passedPercentInput: number,
): RankingDisplayModel {
  const passedPercent = clampPercent(passedPercentInput);
  return buildRankingDisplayFromTopPercent(100 - passedPercent);
}

/**
 * Default entry: API `percentile` is Top % (lower = better).
 */
export function buildRankingDisplay(
  topPercentInput: number,
): RankingDisplayModel {
  return buildRankingDisplayFromTopPercent(topPercentInput);
}

export function getIndustryRank(topPercent: number): IndustryRank {
  const p = clampPercent(topPercent);
  if (p <= 10) return "elite";
  if (p <= 25) return "outstanding";
  if (p <= 50) return "strong";
  if (p <= 75) return "developing";
  return "emerging";
}

/** @deprecated Use buildRankingDisplay(...).isOutstanding */
export function shouldShowOutperformingCopy(topPercent: number): boolean {
  return clampPercent(topPercent) <= OUTSTANDING_TOP_PERCENT_MAX;
}

export function getIndustryRankSegment(
  topPercent: number,
): IndustryRankSegment {
  const rank = getIndustryRank(topPercent);
  return INDUSTRY_RANK_SEGMENTS.find((s) => s.rank === rank)!;
}

/** Marker on the bar (0–100 from left). Left = worst, right = best. */
export function getMarkerPositionPercent(topPercent: number): number {
  return 100 - clampPercent(topPercent);
}

export function getSurpassedPercent(topPercent: number): number {
  return clampPercent(100 - topPercent);
}

export function formatTopPercent(topPercent: number): string {
  return formatPercentOneDecimal(topPercent);
}

/**
 * Ranking is available when we have a finite Top % and an industry label.
 * API `percentile` = Top % (e.g. 6.6 → Top 6.6%).
 */
export function getAvailableRanking(
  topPercent?: number | null,
  industry?: string | null,
): { topPercent: number; industry: string } | null {
  const trimmedIndustry = industry?.trim();
  if (
    typeof topPercent !== "number" ||
    !Number.isFinite(topPercent) ||
    !trimmedIndustry
  ) {
    return null;
  }
  return { topPercent, industry: trimmedIndustry };
}

export function isRankingAvailable(
  topPercent?: number | null,
  industry?: string | null,
): boolean {
  return getAvailableRanking(topPercent, industry) !== null;
}

/** @deprecated Use OUTSTANDING_TOP_PERCENT_MAX */
export const OUTPERFORMING_TOP_PERCENT_MAX = OUTSTANDING_TOP_PERCENT_MAX;

export async function copyToClipboard(
  url: string,
  messages: { success: string; error: string },
): Promise<boolean> {
  if (!navigator.clipboard?.writeText || !window.isSecureContext) {
    toaster.error({ title: messages.error });
    return false;
  }

  try {
    await navigator.clipboard.writeText(url);
    toaster.success({ title: messages.success });
    return true;
  } catch {
    toaster.error({ title: messages.error });
    return false;
  }
}
